import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { BsExclamationCircle, BsHandThumbsDown, BsHandThumbsUp } from "react-icons/bs";
import Textarea from "../components/ui/Textarea";
import { IGroup, RequestState } from "../types/ApiTypes";
import Loading from "../components/Loading";
import { useForm } from "react-hook-form";
import { GroupsContext } from "../context/GroupsContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAccount } from "@azure/msal-react";
import useApi from "../hooks/useApi";
import axios from "axios";
import handleToastError from "../helpers/handleToastError";
import { AppContext } from "../context/AppContext";

const SpaceAcceptance: FC = () => {
  const [space, setSpace] = useState<IGroup>();
  const [spaceLoading, setSpaceLoading] = useState(false);
  const { register, watch, reset, trigger, formState: { errors, isValid } } = useForm<{ reason: string }>({
    mode: "onChange"
  });

  const params = useParams<{ id: string }>();
  const account = useAccount();
  const navigate = useNavigate();
  const groupCtx = useContext(GroupsContext);
  const appCtx = useContext(AppContext);
  const { changeSpaceAcceptance, fetchSpaceById } = useApi();

  const isOwner = useCallback((space: IGroup) => !!space && !!account && (space.primary_owner === account.localAccountId || space.secondary_owner === account.localAccountId),[account])
  const isAdmin = appCtx.state.roles.includes('ROLE_ADMIN');

  useEffect(() => {
    (async () => {
      if(!params.id) return;
      setSpaceLoading(true);
      if (groupCtx.state.groups.length > 0 && account) {
        let group = groupCtx.state.groups.find(x => x.requestId === params.id);
        if (!group) {
          group = await fetchSpaceById(params.id)
        }
        if(!group) {
          toast.error("Space with ID: " + params.id + " wurde nicht gefunden");
          return navigate("/");
        }
        setSpace(group);
        if (!isOwner(group) && !isAdmin) {
          toast.error("Du darfst dieses Space nicht bestätigen");
          return navigate(-1);
        }
        setSpaceLoading(false);
      }
    })();
  }, [groupCtx.state.groups, params.id, navigate, account]);

  const onSubmit = async (type: "accept" | "decline" | "force") => {
    await trigger();
    if (errors.reason || !isValid) return;
    await toast.promise(changeSpaceAcceptance({ state: type, reason: watch("reason") }, params.id!),
      {
        loading: "Der Formular wird abgeschickt",
        success: (res) => {
          groupCtx.dispatch({ type: "REPLACE_GROUP", payload: res });
          reset();
          return `Du hast das Space erfolgreich ${type === "decline" ? "abgelehnt" : "bestätigt"}`;
        },
        error: (err) => handleToastError(err, "An error occurred! 😥")
      });
    navigate("/space/" + params.id);
  };

  return (
    (!spaceLoading && space) ? (
      <section className="grid grid-cols-1 lg:grid-cols-6">
        <div className="lg:col-span-4 lg:col-start-2">
          <Card title={`Bestätige das Space ${space.type} ${space.name}`}>
            <form>
              <div>
                <label className="font-semibold" htmlFor="reason">Begründung</label>
                <Textarea label="reason" id="reason" {...register("reason", { required: true, minLength: 10 })} />
                {errors.reason ?
                  <small className="text-error-brighter">Die Begründung muss mindestens 10 Zeichen lang sein</small> :
                  <small>Deine Entscheidung benötigt eine Begründung</small>}
              </div>
              <div className="flex gap-4 mt-2 flex-col xl:flex-row">
                <Button type="button" onClick={_ => onSubmit("accept")}
                        className="bg-success-darker hover:shadow-success-darker/20"><BsHandThumbsUp
                  className="text-xl mr-2" />Bestätigen</Button>
                <Button type="button" onClick={_ => onSubmit("decline")}
                        className="bg-error-default hover:shadow-error-default/20"><BsHandThumbsDown
                  className="text-xl mr-2" />Ablehnen</Button>
                {space.request_state === RequestState.AwaitsAcceptance &&
                  <Button type="button" onClick={_ => onSubmit("force")}
                          className="bg-orange-700 hover:shadow-error-default/20"><BsExclamationCircle
                    className="text-xl mr-2" />Forcieren</Button>}
              </div>
            </form>
          </Card>
        </div>
      </section>
    ) : (<Loading />)
  );
};

export default SpaceAcceptance;
